import { ref, toRef, toRefs, computed, defineComponent, watch, reactive, onMounted, inject } from "vue"
import { useAuth } from "./useAuth"
import { setError, startLoading, stopLoading } from "../components/Notification"
import { usePaging } from "../components/usePaging"
import { EMPTY_GUID } from "../utils/constants"

export const useCalendar = (defaultFilters, { syncQueryParams = false } = {}) => {
  const { fetchAuthAsync } = useAuth()
  const events = ref([])
  const router = VueRouter.useRouter()
  const route = VueRouter.useRoute()

  const DEFAULT_FILTERS = () => {
    let overrideDefaults = null
    if (typeof defaultFilters === "function") overrideDefaults = defaultFilters()

    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, "0")
    const lastDay = new Date(year, now.getMonth() + 1, 0).getDate()
    const day = String(lastDay).padStart(2, "0")

    return {
      customer: overrideDefaults?.customer ?? EMPTY_GUID,
      facility: overrideDefaults?.facility ?? EMPTY_GUID,
      dateFrom: overrideDefaults?.dateFrom ?? `${year}-${month}-01`,
      dateTo: overrideDefaults?.dateTo ?? `${year}-${month}-${day}`,
    }
  }

  const applyQueryParams = () => {
    const q = route.query
    if (q.customerID) filters.value.customer = q.customerID
    if (q.facilityID) filters.value.facility = q.facilityID
    if (q.dateFrom) filters.value.dateFrom = q.dateFrom
    if (q.dateTo) filters.value.dateTo = q.dateTo
  }
  const resetFilters = async () => {
    filters.value = DEFAULT_FILTERS()
    if (syncQueryParams) applyQueryParams()
  }
  const filters = ref(DEFAULT_FILTERS())

  // Restore from URL on first load
  if (syncQueryParams) applyQueryParams()

  const generateCalendarUrl = (all = false) => {
    var url = `${AZURE_URI}/Calendar?1=1`
    if (!all) {
      url += `&pageNum=${paging.value.pageNum}&itemsPerPage=${paging.value.itemsPerPage}`
    } else {
      url += `&pageNum=1&itemsPerPage=65536`
    }
    if (filters.value.customer && filters.value.customer !== EMPTY_GUID) {
      url += `&customerId=${filters.value.customer}`
    }
    if (filters.value.facility && filters.value.facility !== EMPTY_GUID) {
      url += `&facilityId=${filters.value.facility}`
    }
    if (filters.value.dateFrom) {
      url += `&dateFrom=${filters.value.dateFrom}`
    }
    if (filters.value.dateTo) {
      url += `&dateTo=${filters.value.dateTo}`
    }
    return url
  }

  const getEventsAsync = async () => {
    startLoading("getEventsAsync")
    try {
      const url = generateCalendarUrl()
      const raw = await fetchAuthAsync(url, "GET")
      if (!Array.isArray(raw.result)) {
        throw raw
      }
      setNumberOfRecords(raw.numberOfRecords)
      events.value = raw.result
    } catch (e) {
      setError(e)
      events.value = []
    }
    stopLoading("getEventsAsync")
  }

  const { paging, setNumberOfRecords, setPageNum, resetPaging } = usePaging(getEventsAsync)

  watch(
    () => ({ ...filters.value }),
    (f) => {
      if (syncQueryParams) {
        const D = DEFAULT_FILTERS()
        const q = {}
        if (f.customer && f.customer !== EMPTY_GUID) q.customerID = f.customer
        if (f.facility && f.facility !== EMPTY_GUID) q.facilityID = f.facility
        if (f.dateFrom && f.dateFrom !== D.dateFrom) q.dateFrom = f.dateFrom
        if (f.dateTo && f.dateTo !== D.dateTo) q.dateTo = f.dateTo
        router.replace({ query: q }).catch(() => {})
      }
      resetPaging()
    },
  )
  onMounted(() => resetPaging())

  return {
    paging,
    resetPaging,
    events,
    filters,
    setPageNum,
    resetFilters,
    getEventsAsync,
    DEFAULT_FILTERS,
  }
}
